/**
 * Runtime UI translation fallback.
 *
 * When the `t()` helper is asked for a key that has no pre-translated value
 * (i.e. only the English source exists), this module:
 *   1. immediately returns the English string so the UI never blocks,
 *   2. queues the (lang, key) pair for a batched server-side translation,
 *   3. caches the result in memory + localStorage, and
 *   4. notifies subscribers so React components re-render with the new text.
 *
 * One round-trip handles up to 60 keys per language; batches are debounced
 * by ~80ms so a single render that misses many keys triggers one request.
 */

import { translateUiStrings } from "@/lib/translate-ui.functions";

type Lang = string;
type Key = string;

const STORAGE_KEY = "what.ui-translations.v2";
const VERSION_STORAGE_KEY = "what.ui-translations.cache-version";
const BATCH_MS = 20;
const MAX_BATCH = 60;

// in-memory cache: lang -> key -> translation
const cache: Record<Lang, Record<Key, string>> = {};

// pending keys per lang waiting to be flushed
const pending: Record<Lang, Map<Key, string>> = {};

// inflight (lang|key) to dedupe across batches
const inflight = new Set<string>();

// failure tracking — `lang|key` -> { count, nextRetryAt }. Caller still gets
// English; we just stop hammering the API when it's failing.
const failures = new Map<string, { count: number; nextRetryAt: number }>();
const MAX_RETRIES = 3;
const FLUSH_TIMEOUT_MS = 8000;

let flushTimer: ReturnType<typeof setTimeout> | null = null;

// subscribers — React components register here to re-render when translations arrive
const subscribers = new Set<() => void>();
let version = 0;

function notify() {
  version++;
  for (const fn of subscribers) fn();
}

function loadFromStorage() {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      for (const lang of Object.keys(parsed)) {
        if (parsed[lang] && typeof parsed[lang] === "object") {
          cache[lang] = { ...parsed[lang] };
        }
      }
    }
  } catch {
    /* noop */
  }
}

function saveToStorage() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    /* quota — fine, in-memory cache still works */
  }
}

/**
 * Wipe the runtime + localStorage translation cache. Called when the server
 * reports a newer `translation_cache_meta.version` than what this browser
 * last saw — guarantees admin-edited text appears in every language without
 * users having to clear site data manually.
 */
export function clearTranslationCache() {
  for (const lang of Object.keys(cache)) delete cache[lang];
  failures.clear();
  if (typeof window !== "undefined") {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  }
  notify();
}

/** Compare server cache-version against the one stored locally; reset if newer. */
export function reconcileCacheVersion(serverVersion: number) {
  if (typeof window === "undefined") return;
  if (!Number.isFinite(serverVersion) || serverVersion <= 0) return;
  let stored = 0;
  try {
    const raw = window.localStorage.getItem(VERSION_STORAGE_KEY);
    if (raw) stored = parseInt(raw, 10) || 0;
  } catch { /* noop */ }
  if (stored === serverVersion) return;
  // First-ever read: just record it, no need to wipe.
  if (stored !== 0 && stored !== serverVersion) clearTranslationCache();
  try { window.localStorage.setItem(VERSION_STORAGE_KEY, String(serverVersion)); } catch { /* noop */ }
}

let hydrated = false;
function ensureHydrated() {
  if (hydrated) return;
  hydrated = true;
  loadFromStorage();
}

async function flushLang(lang: Lang) {
  const queue = pending[lang];
  if (!queue || queue.size === 0) return;
  // take up to MAX_BATCH
  const items: Record<string, string> = {};
  let i = 0;
  for (const [k, v] of queue) {
    items[k] = v;
    queue.delete(k);
    i++;
    if (i >= MAX_BATCH) break;
  }

  const keys = Object.keys(items);
  for (const k of keys) inflight.add(`${lang}|${k}`);

  try {
    // Race the request against a timeout so a hung API never wedges the queue
    // — components keep showing English instead of a forever-pending state.
    const res = await Promise.race([
      translateUiStrings({ data: { lang, items } }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("translate timeout")), FLUSH_TIMEOUT_MS)
      ),
    ]);
    if (res?.translations) {
      cache[lang] = { ...(cache[lang] ?? {}), ...res.translations };
      saveToStorage();
      notify();
      // clear failure state for any keys that came back successfully
      for (const k of keys) {
        if (res.translations[k]) failures.delete(`${lang}|${k}`);
      }
    }
  } catch (err) {
    console.warn("[runtime-translate] flush failed, falling back to English:", err);
    // record failure with exponential backoff so we don't hammer a broken API
    const now = Date.now();
    for (const k of keys) {
      const flightKey = `${lang}|${k}`;
      const prev = failures.get(flightKey);
      const count = (prev?.count ?? 0) + 1;
      const backoff = Math.min(60_000, 1000 * 2 ** count);
      failures.set(flightKey, { count, nextRetryAt: now + backoff });
    }
  } finally {
    for (const k of keys) inflight.delete(`${lang}|${k}`);
    // chain more if queue still has items
    if (queue.size > 0) scheduleFlush(lang);
  }
}

function scheduleFlush(lang: Lang) {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    for (const l of Object.keys(pending)) void flushLang(l);
  }, BATCH_MS);
}

/**
 * Look up a translation in the runtime cache. If missing, queue it for
 * background translation and return undefined so the caller can fall back
 * to English.
 */
/** Warm the in-memory + localStorage cache so the next language switch is instant. */
export function seedRuntimeTranslations(lang: Lang, map: Record<string, string>) {
  if (!lang || lang === "en" || !map) return;
  ensureHydrated();
  cache[lang] = { ...(cache[lang] ?? {}), ...map };
  saveToStorage();
}

export function getRuntimeTranslation(lang: Lang, key: Key, englishSource: string): string | undefined {
  if (typeof window === "undefined") return undefined; // SSR — never queue
  if (!lang || lang === "en") return undefined;
  if (!englishSource || !englishSource.trim()) return undefined;

  ensureHydrated();

  const hit = cache[lang]?.[key];
  // Treat a cached value identical to the English source as a miss — an
  // earlier translation attempt may have stored the source verbatim (e.g.
  // model returned the input unchanged), which would otherwise pin the UI
  // to English forever even after admin edits or backend cache fixes.
  if (typeof hit === "string" && hit.length > 0 && hit.trim() !== englishSource.trim()) return hit;

  // queue (dedupe)
  const flightKey = `${lang}|${key}`;
  if (inflight.has(flightKey)) return undefined;

  // back off if this key has been failing — caller still gets English
  const fail = failures.get(flightKey);
  if (fail) {
    if (fail.count >= MAX_RETRIES) return undefined;
    if (Date.now() < fail.nextRetryAt) return undefined;
  }

  if (!pending[lang]) pending[lang] = new Map();
  if (!pending[lang].has(key)) {
    pending[lang].set(key, englishSource);
    scheduleFlush(lang);
  }
  return undefined;
}

/** Subscribe to translation-arrival events. Returns an unsubscribe function. */
export function subscribeToTranslations(fn: () => void): () => void {
  subscribers.add(fn);
  return () => { subscribers.delete(fn); };
}

/** Snapshot version used by useSyncExternalStore. */
export function getTranslationsVersion(): number {
  return version;
}
